import React, { useEffect, useState } from 'react';
import DetailItem from './DetailItem';
import '../Entreprise/Detailsmission/Detailsmission.css';
import ActionButtons from './ActionButtons';
import ChipInput from './ChipInput';
import DisponibilitePicker from './DisponibilitePicker';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Box,
} from '@mui/material';
import {
  languageOptions,
  languageLevelOptions,
  contractTypeOptions,
} from './constants/options';
import Avatar from '@mui/material/Avatar';
import { fetchSkillsFromLightcast } from '../../services/lightcastApi';

const ProfilRecherche = ({
  editedMission,
  editable,
  onChange,
  onSave,
  onToggleEdit,
  showActionButtons = false,
  entrepriseLogo
}) => {
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [secteurOptions, setSecteurOptions] = useState([
    'Finance', 'Santé', 'Industrie', 'IT', 'Télécom'
  ]);

  const handleSkillInputChange = async (input) => {
    if (input?.length >= 2) {
      try {
        const suggestions = await fetchSkillsFromLightcast(input);
        setSkillSuggestions(suggestions.map((s) => s.label || s.nom || s));
      } catch (error) {
        console.error("Erreur lors du chargement des compétences :", error);
      }
    }
  };

  return (
    <article className="missionContainer">
      <header className="missionHeader updated-layout">
        <div className="mission-header-left">
          <Avatar
            variant="rounded"
            src={entrepriseLogo ? `${process.env.REACT_APP_API_BASE_URL}${entrepriseLogo}` : undefined}
            alt={editedMission?.NomEntreprise || "Entreprise"}
            className="logo-avatar"
          >
            {editedMission?.entreprise?.NomEntreprise?.[0]?.toUpperCase() || "E"}
          </Avatar>

          {editable ? (
            <TextField
              label="Objet"
              value={editedMission.nomMission}
              onChange={(e) => onChange('nomMission', e.target.value)}
              className="missionTitleInput"
              fullWidth
            />
          ) : (
            <h1 className="missionTitle">{editedMission.nomMission}</h1>
          )}
        </div>

        {showActionButtons && (
          <div className="action-buttons-container">
            <ActionButtons
              isEditing={editable}
              onEdit={onToggleEdit}
              onSave={onSave}
              onCancel={onToggleEdit}
              showDelete={false}
              showDownload={false}
              showView={false}
            />
          </div>
        )}
      </header>

      <div className="profilGrid">
        <div className="profilColumn">
          {editable ? (
            <>
              <ChipInput
                label="Compétences clés"
                values={editedMission.competences || []}
                onChange={(v) => onChange('competences', v)}
                suggestions={skillSuggestions}
                onInputChange={handleSkillInputChange}
              />

              <TextField
                label="Formation / Diplômes"
                value={editedMission.formation}
                onChange={(e) => onChange('formation', e.target.value)}
                fullWidth
                margin="normal"
              />

              <ChipInput
                label="Types de contrat *"
                required
                values={editedMission.typeContrat || []}
                onChange={(newValues) => onChange('typeContrat', newValues)}
                suggestions={contractTypeOptions.map((option) => option.label)}
              />

              <div className="langues-section">
                {(editedMission.langues || []).map((entry, index) => (
                  <div className="langue-row" key={index}>
                    <FormControl className="step-two-half">
                      <InputLabel>Langue</InputLabel>
                      <Select
                        value={entry.langue || ''}
                        onChange={(e) => {
                          const updated = [...editedMission.langues];
                          updated[index].langue = e.target.value;
                          onChange('langues', updated);
                        }}
                      >
                        {languageOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl className="step-two-half">
                      <InputLabel>Niveau</InputLabel>
                      <Select
                        value={entry.niveau || ''}
                        onChange={(e) => {
                          const updated = [...editedMission.langues];
                          updated[index].niveau = e.target.value;
                          onChange('langues', updated);
                        }}
                      >
                        {languageLevelOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="text"
                      color="error"
                      onClick={() => {
                        const updated = [...editedMission.langues];
                        updated.splice(index, 1);
                        onChange('langues', updated);
                      }}
                      className="delete-langue-btn"
                    >
                      Supprimer
                    </Button>
                  </div>
                ))}

                <Button
                  variant="outlined"
                  onClick={() =>
                    onChange('langues', [
                      ...(editedMission.langues || []),
                      { langue: '', niveau: '' },
                    ])
                  }
                  className="ajouter-langue-btn"
                >
                  + Ajouter une langue
                </Button>
              </div>
            </>
          ) : (
            <>
              {editedMission.competences?.length > 0 && (
                <DetailItem
                  label="Compétences clés"
                  value={editedMission.competences.join(', ')}
                />
              )}
              {editedMission.formation && (
                <DetailItem
                  label="Formation / Diplômes"
                  value={editedMission.formation}
                />
              )}
              {editedMission.typeContrat && (
                <DetailItem
                  label="Type de contrat"
                  value={editedMission.typeContrat.join(', ')}
                />
              )}
              {editedMission.langues?.length > 0 && (
                <DetailItem
                  label="Langues requises"
                  value={editedMission.langues
                    .map((l) => `${l.langue} (${l.niveau})`)
                    .join(', ')}
                />
              )}
            </>
          )}
        </div>

        <div className="profilColumn">
          {editable ? (
            <>
              <TextField
                label="Expérience souhaitée"
                value={editedMission.experience}
                onChange={(e) => onChange('experience', e.target.value)}
                fullWidth
                margin="normal"
              />

              <DisponibilitePicker
                label="Disponibilité idéale du candidat"
                value={editedMission.disponibilite}
                onChange={(val) => onChange('disponibilite', val)}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Secteur d'activité</InputLabel>
                <Select
                  value={editedMission.secteur || ''}
                  onChange={(e) => onChange('secteur', e.target.value)}
                  label="Secteur d'activité"
                >
                  {secteurOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Champ Informations complémentaires */}
              <TextField
                label="Informations complémentaires"
                value={editedMission.infoComplementaires || ''}
                onChange={e => onChange('infoComplementaires', e.target.value)}
                fullWidth
                margin="normal"
                multiline
                minRows={3}
              />
            </>
          ) : (
            <>
              {editedMission.experience && (
                <DetailItem
                  label="Expérience souhaitée"
                  value={editedMission.experience}
                />
              )}
              {editedMission.disponibilite && (
                <DetailItem
                  label="Disponibilité idéale"
                  value={new Date(editedMission.disponibilite).toLocaleDateString('fr-FR')}
                />
              )}
              {editedMission.secteur && (
                <DetailItem label="Secteur d'activité" value={editedMission.secteur} />
              )}
              {editedMission.infoComplementaires && (
                <DetailItem
                  label="Informations complémentaires"
                  value={editedMission.infoComplementaires}
                />
              )}
            </>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProfilRecherche;
